// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL CALCULATIONS — Pure functies voor Business Case module
// ═══════════════════════════════════════════════════════════════════════════

import type { FinancialParams } from './financialDefaults';
import { FINANCIAL_RANGES } from './financialDefaults';

export interface SystemConfig {
  pvCapacity: number;    // MWp
  bessEnergy: number;    // MWh
  bessPower: number;     // MW
  backupPower: number;   // MW
  gridMax: number;       // MW
  numTrucks: number;     // count
}

export interface SimulationResults {
  pvProduction: number;  // MWh/year
  /**
   * MWh/jaar — de LAADVRAAG (`r.dem`), inclusief het deel dat de simulatie niet
   * kon leveren. Dit is géén levering: gebruik `deliveredEnergy(sim)` zodra je
   * ergens een bedrag aan wilt hangen.
   */
  totalDelivered: number;
  gridImport: number;    // MWh/year
  gridExport: number;    // MWh/year
  backupUsed: number;    // MWh/year
  curtailment: number;   // MWh/year
  bLoss: number;         // MWh/year
  blokstroomImport?: number; // MWh/year — grid import during blokstroom hours
  /**
   * MWh/jaar vraag die net, batterij én noodaggregaat samen niet konden dekken
   * (de `unmet` uit de simulatielus). Verplicht veld: zonder dit getal is niet
   * te bepalen wat er werkelijk geleverd is, en dus ook niet wat er gefactureerd
   * mag worden. Zie `deliveredEnergy` voor het waarom en de gemeten cijfers.
   */
  unmet: number;
}

/**
 * Werkelijk geleverde energie (MWh/jaar) = laadvraag − ongedekte vraag.
 *
 * WAAROM deze functie bestaat: `totalDelivered` werd gevuld met `r.dem` — de
 * VRAAG, niet de levering — terwijl de simulatielus in `HubSimulator.tsx` het
 * tekort tien regels eerder al als `unmet` wegschreef en optelde in `tUnmet`.
 * Op case SL 18-3 (Simon Loos / Wognum: 2 MWp PV, 20 MWh BESS, aansluiting 0,33 MW,
 * 19.000 MWh vraag) markeert het model 14.840 van de 18.994 MWh als ongedekt —
 * 78,1 % — en die werd voluit gefactureerd tegen €0,30 laadtarief plus €0,12
 * ERE. Dat gaf jaar-1 omzet €7.977.597 en NPV €75.304.375 op een investering
 * van €5.483.500, terugverdientijd 0,76 jaar. Met levering = 18.994 − 14.840
 * blijft er 4.154 MWh over die daadwerkelijk verkocht kan worden.
 *
 * Ontbreekt `unmet`, dan is het antwoord NIET nul en ook niet de vraag, maar
 * NaN — onbekend. Dat propageert zichtbaar door omzet → EBITDA → NPV/IRR, zodat
 * een caller die het veld vergeet dat op zijn scherm ziet in plaats van stil
 * terug te vallen op de oude, te hoge cijfers.
 */
export function deliveredEnergy(sim: SimulationResults): number {
  if (typeof sim.unmet !== 'number' || !Number.isFinite(sim.unmet)) return NaN;
  return Math.max(0, sim.totalDelivered - sim.unmet);
}

/**
 * Toont een €/kWh-getal, of "n.v.t." als het niet bestaat.
 *
 * Zowel LCOE als marge/kWh kunnen legitiem niet-eindig zijn: bij nul geleverde
 * energie zijn de kosten niet over kWh's te verdelen (LCOE = Infinity, marge =
 * −Infinity), en bij een ontbrekende `unmet` is de levering onbekend (NaN).
 * `toFixed()` maakt daar "€NaN/kWh" of "€Infinity/kWh" van. Deze functie staat
 * hier — naast de rekenregel die die waarden produceert — zodat elke plek die
 * een LCOE of marge toont hetzelfde antwoord geeft.
 */
export function formatEurPerKwh(value: number, decimals = 3): string {
  if (!Number.isFinite(value)) return 'n.v.t.';
  return `€${value.toFixed(decimals).replace('.', ',')}/kWh`;
}

export interface CapexBreakdown {
  pv: number;
  bess: number;
  backup: number;
  chargers: number;
  gridConnection: number;
  customCapex: number;
  subtotal: number;
  engineering: number;
  total: number;
}

export interface YearCashflow {
  year: number;
  // Revenue
  chargingRevenue: number;
  exportRevenue: number;
  sdeRevenue: number;
  ereRevenue: number;
  totalRevenue: number;
  // Costs
  gridCost: number;
  blokstroomCost: number;
  gridOperatorCost: number;
  fuelCost: number;
  omPv: number;
  omBess: number;
  insurance: number;
  locationRent: number;
  customOpex: number;
  bessReplacement: number;
  debtService: number;
  totalOpex: number;
  // Net
  ebitda: number;
  netCashflow: number;
  cumulativeCashflow: number;
  discountedCashflow: number;
  dscr: number;
  // CO2
  co2Avoided: number;
  // Fiscal
  depreciation: number;
  taxableIncome: number;
  vpb: number;
  fiscalIncentive: number;
}

export interface FinancialKpis {
  npv: number;
  irr: number;
  equityIrr: number;
  paybackYear: number | null;
  lcoe: number;
  marginPerKwh: number;
  roi: number;
  totalCapex: number;
  totalCo2Avoided: number;
  co2CertificateValue: number;
  avgDscr: number;
  minDscr: number;
  debtAmount: number;
  equityAmount: number;
  npvAfterTax: number;
  irrAfterTax: number;
  projectDuration: number;
}

export interface SensitivityItem {
  param: string;
  label: string;
  baseNpv: number;
  lowNpv: number;
  highNpv: number;
  impactLow: number;
  impactHigh: number;
}

// ── Energy price factor (non-linear) ────────────────────────────────────

function energyPriceFactor(year: number, baseInflation: number, volatility: number): number {
  const v = volatility / 100;
  const linearFactor = Math.pow(1 + baseInflation / 100, year - 1);
  if (v <= 0) return linearFactor;
  const boost = v * 0.03;
  const earlyYears = Math.min(year - 1, 5);
  const lateYears = Math.max(0, year - 1 - 5);
  const earlyFactor = Math.pow(1 + baseInflation / 100 + boost, earlyYears);
  const lateFactor = Math.pow(1 + baseInflation / 100 - boost * 0.3, lateYears);
  return earlyFactor * lateFactor;
}

// ── CAPEX ────────────────────────────────────────────────────────────────

export function calculateCapex(sys: SystemConfig, fp: FinancialParams): CapexBreakdown {
  const pv = sys.pvCapacity * 1000 * fp.pvCapex;
  const bess = sys.bessEnergy * 1000 * fp.bessCapex;
  const backup = sys.backupPower * 1000 * fp.backupCapex;
  const chargers = fp.chargerDetailConfig && fp.chargerDetailConfig.length > 0
    ? fp.chargerDetailConfig.reduce((sum, c) => sum + c.count * c.pricePerUnit, 0)
    : sys.numTrucks * fp.chargerCapex;
  const gridConnection = fp.gridConnectionCapex;
  const customCapex = (fp.customCapexItems ?? []).reduce((sum, item) => sum + (item.amount || 0), 0);
  const subtotal = pv + bess + backup + chargers + gridConnection + customCapex;
  const engineering = subtotal * (fp.engineeringPct / 100);
  return { pv, bess, backup, chargers, gridConnection, customCapex, subtotal, engineering, total: subtotal + engineering };
}

// ── Debt service (annuity) ──────────────────────────────────────────────

function annualDebtService(totalCapex: number, debtRatio: number, interestRate: number, loanTerm: number): number {
  const principal = totalCapex * (debtRatio / 100);
  if (principal <= 0 || loanTerm <= 0) return 0;
  const r = interestRate / 100;
  if (r <= 0) return principal / loanTerm;
  return principal * (r * Math.pow(1 + r, loanTerm)) / (Math.pow(1 + r, loanTerm) - 1);
}

// ── CASHFLOW ARRAY ───────────────────────────────────────────────────────

export function generateCashflows(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number
): YearCashflow[] {
  const capex = calculateCapex(sys, fp);
  const years: YearCashflow[] = [];
  let cumulative = -capex.total;
  const ds = annualDebtService(capex.total, fp.debtRatio, fp.interestRate, fp.loanTerm);

  // Fiscal: EIA on PV+BESS, MIA on chargers — spread over max 5 years, capped at VPB per year
  const eiaBase = capex.pv + capex.bess;
  const miaBase = capex.chargers;
  const totalFiscalIncentive = eiaBase * (fp.eiaPercentage / 100) * (fp.vpbRate / 100)
    + miaBase * (fp.miaPercentage / 100) * (fp.vpbRate / 100);
  const annualDepreciation = capex.total / fp.depreciationYears;

  // Year 0
  years.push({
    year: 0,
    chargingRevenue: 0, exportRevenue: 0, sdeRevenue: 0, ereRevenue: 0, totalRevenue: 0,
    gridCost: 0, blokstroomCost: 0, gridOperatorCost: 0, fuelCost: 0, omPv: 0, omBess: 0, insurance: 0,
    locationRent: 0, customOpex: 0,
    bessReplacement: 0, debtService: 0,
    totalOpex: 0, ebitda: 0,
    netCashflow: -capex.total,
    cumulativeCashflow: cumulative,
    discountedCashflow: -capex.total,
    dscr: 0, co2Avoided: 0,
    depreciation: 0, taxableIncome: 0, vpb: 0, fiscalIncentive: 0,
  });

  // Pass 1: calculate base cashflows without fiscal incentive to determine VPB per year
  const baseCfs: { ebitda: number; depreciation: number; vpb: number; debtService: number; co2Avoided: number;
    chargingRevenue: number; exportRevenue: number; sdeRevenue: number; ereRevenue: number; totalRevenue: number;
    gridCost: number; blokstroomCost: number; gridOperatorCost: number; fuelCost: number; omPv: number; omBess: number; insurance: number;
    locationRent: number; customOpex: number; bessReplacement: number;
    totalOpex: number; discFactor: number }[] = [];

  // Blokstroom: split grid import into regular and blokstroom portions
  const blokstroomImportGwh = sim.blokstroomImport ?? 0;
  const regularImportGwh = sim.gridImport - blokstroomImportGwh;

  // Custom OPEX total
  const customOpexAnnual = (fp.customOpexItems ?? []).reduce((s, item) => s + item.annualCost, 0);

  // Omzetbasis: geleverd, niet gevraagd. Op SL 18-3 scheelt dat 14.840 van de
  // 18.994 MWh (78,1 %) die het model zelf als ongedekt markeert — die kan niet
  // gefactureerd worden tegen laadtarief of ERE. Zie `deliveredEnergy`.
  const delivered = deliveredEnergy(sim);

  for (let y = 1; y <= fp.projectDuration; y++) {
    const degFactor = Math.pow(1 - degradation / 100, y - 1);
    const inflFactor = Math.pow(1 + fp.inflation / 100, y - 1);
    const escFactor = Math.pow(1 + fp.tariffEscalation / 100, y - 1);
    const ePriceFactor = energyPriceFactor(y, fp.inflation, fp.energyPriceVolatility);
    const discFactor = Math.pow(1 + fp.wacc / 100, y);

    const deliveredDeg = delivered * degFactor;
    const exportDeg = sim.gridExport * degFactor;
    const backupDeg = sim.backupUsed * degFactor;
    const pvProdDeg = sim.pvProduction * degFactor;

    const chargingRevenue = deliveredDeg * 1e3 * fp.chargingTariff * escFactor;
    const exportRevenue = exportDeg * 1e3 * fp.gridExportTariff * ePriceFactor;
    const sdeRevenue = y <= fp.sdeMaxYears ? pvProdDeg * 1e3 * fp.sdeSubsidy : 0;
    const ereRevenue = deliveredDeg * 1e3 * fp.ereTariff * escFactor;
    const totalRevenue = chargingRevenue + exportRevenue + sdeRevenue + ereRevenue;

    const gridCost = regularImportGwh * degFactor * 1e3 * fp.gridImportTariff * ePriceFactor;
    const blokstroomCost = blokstroomImportGwh * degFactor * 1e3 * fp.blokstroomTariff * ePriceFactor;
    // Netbeheerdertarieven: transportkosten op basis van gecontracteerd vermogen (gMax in MW → kW)
    const gridOperatorCost = (sys.gridMax * 1000 * fp.gridTransportTariff + fp.gridSystemServiceCost + fp.gridMeteringCost) * inflFactor;
    const fuelCost = backupDeg * 1e3 * fp.backupFuelCost * inflFactor;
    const omPv = capex.pv * (fp.omPvPct / 100) * inflFactor;
    const omBess = sys.bessEnergy * 1000 * fp.omBessPerKwh * inflFactor;
    const insurance = capex.total * (fp.insurancePct / 100) * inflFactor;
    const locationRent = fp.locationRentPerM2 * fp.locationSizeM2 * inflFactor;
    const customOpex = customOpexAnnual * inflFactor;
    const bessReplacement = y === fp.bessReplacementYear
      ? capex.bess * (1 - fp.bessReplacementCostReduction / 100) : 0;
    const totalOpex = gridCost + blokstroomCost + gridOperatorCost + fuelCost + omPv + omBess + insurance + locationRent + customOpex + bessReplacement;
    const ebitda = totalRevenue - totalOpex;

    const depreciation = y <= fp.depreciationYears ? annualDepreciation : 0;
    const taxableIncome = ebitda - depreciation;
    const vpb = Math.max(0, taxableIncome * (fp.vpbRate / 100));
    const debtService = y <= fp.loanTerm ? ds : 0;
    // C3: Conservatieve CO₂-berekening — curtailment levert geen CO₂-reductie
    const pvUsefulDeg = (sim.pvProduction - sim.curtailment) * degFactor;
    const co2Avoided = pvUsefulDeg * 1e3 * fp.co2GridFactor / 1000;

    baseCfs.push({
      ebitda, depreciation, vpb, debtService, co2Avoided,
      chargingRevenue, exportRevenue, sdeRevenue, ereRevenue, totalRevenue,
      gridCost, blokstroomCost, gridOperatorCost, fuelCost, omPv, omBess, insurance, locationRent, customOpex, bessReplacement, totalOpex,
      discFactor,
    });
  }

  // Pass 2: spread EIA/MIA over max 5 years, each year capped at that year's VPB
  let remainingIncentive = totalFiscalIncentive;
  const incentivePerYear: number[] = new Array(fp.projectDuration).fill(0);
  for (let i = 0; i < Math.min(5, fp.projectDuration); i++) {
    if (remainingIncentive <= 0) break;
    const used = Math.min(remainingIncentive, baseCfs[i].vpb);
    incentivePerYear[i] = used;
    remainingIncentive -= used;
  }

  // Pass 3: build final cashflows
  for (let i = 0; i < fp.projectDuration; i++) {
    const y = i + 1;
    const cf = baseCfs[i];
    const fiscalIncentive = incentivePerYear[i];
    const netCashflow = cf.ebitda - cf.debtService - cf.vpb + fiscalIncentive;
    cumulative += netCashflow;
    const dscr = cf.debtService > 0 ? cf.ebitda / cf.debtService : Infinity;

    years.push({
      year: y,
      chargingRevenue: cf.chargingRevenue, exportRevenue: cf.exportRevenue,
      sdeRevenue: cf.sdeRevenue, ereRevenue: cf.ereRevenue, totalRevenue: cf.totalRevenue,
      gridCost: cf.gridCost, blokstroomCost: cf.blokstroomCost, gridOperatorCost: cf.gridOperatorCost, fuelCost: cf.fuelCost, omPv: cf.omPv, omBess: cf.omBess,
      insurance: cf.insurance, locationRent: cf.locationRent, customOpex: cf.customOpex,
      bessReplacement: cf.bessReplacement, debtService: cf.debtService,
      totalOpex: cf.totalOpex, ebitda: cf.ebitda, netCashflow,
      cumulativeCashflow: cumulative,
      discountedCashflow: netCashflow / cf.discFactor,
      dscr, co2Avoided: cf.co2Avoided,
      depreciation: cf.depreciation, taxableIncome: cf.ebitda - cf.depreciation,
      vpb: cf.vpb, fiscalIncentive,
    });
  }

  return years;
}

// ── NPV ──────────────────────────────────────────────────────────────────

export function calculateNpv(cashflows: YearCashflow[]): number {
  return cashflows.reduce((sum, cf) => sum + cf.discountedCashflow, 0);
}

// ── IRR (Newton-Raphson) ─────────────────────────────────────────────────

export function calculateIrr(cashflows: YearCashflow[], maxIter = 100, tolerance = 1e-6): number {
  const cfs = cashflows.map(cf => cf.netCashflow);
  let rate = 0.1;
  for (let i = 0; i < maxIter; i++) {
    let npv = 0;
    let dnpv = 0;
    for (let t = 0; t < cfs.length; t++) {
      const denom = Math.pow(1 + rate, t);
      npv += cfs[t] / denom;
      if (t > 0) dnpv -= t * cfs[t] / Math.pow(1 + rate, t + 1);
    }
    if (Math.abs(dnpv) < 1e-12) break;
    const newRate = rate - npv / dnpv;
    if (Math.abs(newRate - rate) < tolerance) return newRate * 100;
    rate = newRate;
    if (rate < -0.99 || rate > 10) return NaN;
  }
  return rate * 100;
}

// ── EQUITY IRR ───────────────────────────────────────────────────────────

export function calculateEquityIrr(cashflows: YearCashflow[], equityAmount: number, maxIter = 100, tolerance = 1e-6): number {
  const cfs = cashflows.map(cf => cf.year === 0 ? -equityAmount : cf.netCashflow);
  let rate = 0.1;
  for (let i = 0; i < maxIter; i++) {
    let npv = 0;
    let dnpv = 0;
    for (let t = 0; t < cfs.length; t++) {
      const denom = Math.pow(1 + rate, t);
      npv += cfs[t] / denom;
      if (t > 0) dnpv -= t * cfs[t] / Math.pow(1 + rate, t + 1);
    }
    if (Math.abs(dnpv) < 1e-12) break;
    const newRate = rate - npv / dnpv;
    if (Math.abs(newRate - rate) < tolerance) return newRate * 100;
    rate = newRate;
    if (rate < -0.99 || rate > 10) return NaN;
  }
  return rate * 100;
}

// ── PAYBACK ──────────────────────────────────────────────────────────────

export function calculatePayback(cashflows: YearCashflow[]): number | null {
  for (let i = 1; i < cashflows.length; i++) {
    if (cashflows[i].cumulativeCashflow >= 0) {
      const prev = cashflows[i - 1].cumulativeCashflow;
      const curr = cashflows[i].cumulativeCashflow;
      const frac = prev < 0 ? Math.abs(prev) / (Math.abs(prev) + curr) : 0;
      return cashflows[i].year - 1 + frac;
    }
  }
  return null;
}

// ── LCOE (Discounted) ───────────────────────────────────────────────────

export function calculateLcoe(
  cashflows: YearCashflow[],
  sim: SimulationResults,
  fp: FinancialParams,
  degradation: number
): number {
  // Noemer = geleverde kWh, niet gevraagde kWh. Met de vraag als noemer viel de
  // LCOE op SL 18-3 op €0,0662/kWh; die deelt de kosten uit over 14.840 MWh die
  // nooit geleverd wordt, en maakt de marge (chargingTariff − lcoe) daarmee te mooi.
  const delivered = deliveredEnergy(sim);
  // Onbekende levering → onbekende LCOE. Niet 0: een LCOE van 0 leest als
  // "gratis" en zou via marginPerKwh een winst van het volle laadtarief tonen.
  if (!Number.isFinite(delivered)) return NaN;

  let discCosts = Math.abs(cashflows[0].netCashflow);
  let discEnergy = 0;
  for (let y = 1; y < cashflows.length; y++) {
    const discFactor = Math.pow(1 + fp.wacc / 100, y);
    discCosts += cashflows[y].totalOpex / discFactor;
    const degFactor = Math.pow(1 - degradation / 100, y - 1);
    discEnergy += (delivered * degFactor * 1e3) / discFactor;
  }
  // Nul geleverde kWh betekent NIET dat de stroom gratis is. De oude
  // `discEnergy > 0 ? ... : 0` gaf hier 0, en `marginPerKwh = chargingTariff −
  // lcoe` maakte daar €0,300/kWh winst van — gemeten op de Wognum-invoer met
  // pv 0, gMax 0, bP 0, buP 0 en 19.000 MWh vraag: unmet 18.994,3 (100 %),
  // geleverd 0, jaar-1 omzet €0, NPV −€14.001.448, ROI −111,6 % en tóch
  // lcoe 0 / marge 0,300. Kosten over nul kWh zijn per kWh oneindig; met
  // Infinity wordt de marge −Infinity en tonen de KPI-kaarten "n.v.t."
  // (zie `formatEurPerKwh`) in plaats van winst.
  if (discEnergy <= 0) return discCosts > 0 ? Infinity : NaN;
  return discCosts / discEnergy;
}

// ── FULL KPI CALCULATION ─────────────────────────────────────────────────

export function calculateAllKpis(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number
): { kpis: FinancialKpis; cashflows: YearCashflow[]; capex: CapexBreakdown } {
  const capex = calculateCapex(sys, fp);
  const cashflows = generateCashflows(sim, sys, fp, degradation);
  const npv = calculateNpv(cashflows);
  const irr = capex.total === 0 ? 0 : calculateIrr(cashflows);
  const paybackYear = calculatePayback(cashflows);
  const lcoe = calculateLcoe(cashflows, sim, fp, degradation);
  const marginPerKwh = fp.chargingTariff - lcoe;
  const totalProfit = cashflows.slice(1).reduce((s, cf) => s + cf.netCashflow, 0);
  const roi = capex.total > 0 ? (totalProfit / capex.total) * 100 : 0;

  const totalCo2Avoided = cashflows.slice(1).reduce((s, cf) => s + cf.co2Avoided, 0);
  const co2CertificateValue = cashflows.slice(1).reduce((s, cf) => s + cf.ereRevenue, 0);

  const dscrYears = cashflows.filter(cf => cf.year > 0 && cf.year <= fp.loanTerm && cf.dscr !== Infinity);
  const avgDscr = dscrYears.length > 0 ? dscrYears.reduce((s, cf) => s + cf.dscr, 0) / dscrYears.length : 0;
  const minDscr = dscrYears.length > 0 ? Math.min(...dscrYears.map(cf => cf.dscr)) : 0;

  const debtAmount = capex.total * (fp.debtRatio / 100);
  const equityAmount = capex.total - debtAmount;

  const equityIrr = equityAmount > 0 ? calculateEquityIrr(cashflows, equityAmount) : irr;

  // After-tax NPV: sum of discounted after-tax cashflows (already in netCashflow which includes VPB)
  const npvAfterTax = npv; // netCashflow already includes -vpb +fiscalIncentive
  const irrAfterTax = irr; // IRR is already based on after-tax netCashflow

  return {
    kpis: {
      npv, irr, equityIrr, paybackYear, lcoe, marginPerKwh, roi, totalCapex: capex.total,
      totalCo2Avoided, co2CertificateValue, avgDscr, minDscr, debtAmount, equityAmount,
      npvAfterTax, irrAfterTax, projectDuration: fp.projectDuration,
    },
    cashflows,
    capex,
  };
}

// ── SENSITIVITY ANALYSIS ─────────────────────────────────────────────────

export function calculateSensitivity(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number,
  variation = 0.2
): SensitivityItem[] {
  const baseResult = calculateAllKpis(sim, sys, fp, degradation);
  const baseNpv = baseResult.kpis.npv;

  const params: { key: keyof FinancialParams; label: string }[] = [
    { key: 'chargingTariff', label: 'Laadtarief' },
    { key: 'pvCapex', label: 'PV CAPEX' },
    { key: 'bessCapex', label: 'BESS CAPEX' },
    { key: 'gridImportTariff', label: 'Import tarief' },
    { key: 'wacc', label: 'WACC' },
    { key: 'sdeSubsidy', label: 'SDE++ subsidie' },
    { key: 'backupFuelCost', label: 'Brandstofkosten' },
    { key: 'chargerCapex', label: 'Laadinfra CAPEX' },
  ];

  return params.map(({ key, label }) => {
    const base = fp[key] as number;
    const low = { ...fp, [key]: base * (1 - variation) };
    const high = { ...fp, [key]: base * (1 + variation) };
    const lowNpv = calculateAllKpis(sim, sys, low, degradation).kpis.npv;
    const highNpv = calculateAllKpis(sim, sys, high, degradation).kpis.npv;
    return { param: key, label, baseNpv, lowNpv, highNpv, impactLow: lowNpv - baseNpv, impactHigh: highNpv - baseNpv };
  }).sort((a, b) => Math.max(Math.abs(b.impactLow), Math.abs(b.impactHigh)) - Math.max(Math.abs(a.impactLow), Math.abs(a.impactHigh)));
}

// ── SENSITIVITY HEATMAP ──────────────────────────────────────────────────

export interface HeatmapCell {
  x: number;
  y: number;
  xLabel: string;
  yLabel: string;
  npv: number;
}

export function calculateSensitivityHeatmap(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number,
  xKey: keyof FinancialParams = 'chargingTariff',
  yKey: keyof FinancialParams = 'bessCapex',
  steps = 7
): { cells: HeatmapCell[]; xValues: number[]; yValues: number[]; xLabel: string; yLabel: string } {
  const xBase = fp[xKey] as number;
  const yBase = fp[yKey] as number;
  const xValues: number[] = [];
  const yValues: number[] = [];

  for (let i = 0; i < steps; i++) {
    const frac = -0.3 + (0.6 * i) / (steps - 1);
    xValues.push(xBase * (1 + frac));
    yValues.push(yBase * (1 + frac));
  }

  const cells: HeatmapCell[] = [];
  for (const yVal of yValues) {
    for (const xVal of xValues) {
      const modFp = { ...fp, [xKey]: xVal, [yKey]: yVal };
      const npv = calculateAllKpis(sim, sys, modFp, degradation).kpis.npv;
      cells.push({
        x: xVal, y: yVal,
        xLabel: xVal >= 1000 ? `${(xVal / 1000).toFixed(0)}k` : xVal.toFixed(2),
        yLabel: yVal >= 1000 ? `${(yVal / 1000).toFixed(0)}k` : yVal.toFixed(2),
        npv,
      });
    }
  }

  return { cells, xValues, yValues, xLabel: xKey, yLabel: yKey };
}

// ── MONTE CARLO with payback distribution ────────────────────────────────

export interface MonteCarloResult {
  npvs: number[];
  paybacks: (number | null)[];
  p10: number; p50: number; p90: number;
  paybackP10: number | null; paybackP50: number | null; paybackP90: number | null;
  mean: number; std: number;
}

export function runMonteCarlo(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number,
  iterations = 1000
): MonteCarloResult {
  const npvs: number[] = [];
  const paybacks: (number | null)[] = [];
  const rng = () => {
    let u = 0, v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };

  for (let i = 0; i < iterations; i++) {
    const modFp: FinancialParams = {
      ...fp,
      chargingTariff: fp.chargingTariff * (1 + rng() * 0.1),
      pvCapex: fp.pvCapex * (1 + rng() * 0.1),
      bessCapex: fp.bessCapex * (1 + rng() * 0.15),
      gridImportTariff: fp.gridImportTariff * (1 + rng() * 0.15),
      inflation: fp.inflation + rng() * 1,
      wacc: fp.wacc + rng() * 1,
    };
    const result = calculateAllKpis(sim, sys, modFp, degradation);
    npvs.push(result.kpis.npv);
    paybacks.push(result.kpis.paybackYear);
  }

  npvs.sort((a, b) => a - b);
  const validPaybacks = paybacks.filter((p): p is number => p !== null).sort((a, b) => a - b);

  const percentile = (arr: number[], p: number) => arr[Math.floor(arr.length * p)] ?? 0;
  const mean = npvs.reduce((s, v) => s + v, 0) / npvs.length;
  const std = Math.sqrt(npvs.reduce((s, v) => s + (v - mean) ** 2, 0) / npvs.length);

  return {
    npvs, paybacks,
    p10: percentile(npvs, 0.1),
    p50: percentile(npvs, 0.5),
    p90: percentile(npvs, 0.9),
    paybackP10: validPaybacks.length > 0 ? percentile(validPaybacks, 0.1) : null,
    paybackP50: validPaybacks.length > 0 ? percentile(validPaybacks, 0.5) : null,
    paybackP90: validPaybacks.length > 0 ? percentile(validPaybacks, 0.9) : null,
    mean, std,
  };
}

// ── GOAL SEEK (bisection) ────────────────────────────────────────────────

export type GoalKpi = 'npv' | 'irr' | 'payback';

export function goalSeek(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number,
  paramKey: keyof FinancialParams,
  targetKpi: GoalKpi,
  targetValue: number,
  maxIter = 50,
  tolerance = 0.01
): { found: boolean; value: number; resultKpi: number } {
  const range = FINANCIAL_RANGES[paramKey]!;
  let lo = range.min;
  let hi = range.max;

  const getKpi = (val: number): number => {
    const modFp = { ...fp, [paramKey]: val };
    const result = calculateAllKpis(sim, sys, modFp, degradation);
    if (targetKpi === 'npv') return result.kpis.npv;
    if (targetKpi === 'irr') return result.kpis.irr;
    if (targetKpi === 'payback') return result.kpis.paybackYear ?? 999;
    return 0;
  };

  // Check monotonicity direction
  const loKpi = getKpi(lo);
  const hiKpi = getKpi(hi);
  const ascending = hiKpi > loKpi;

  // Check if target is reachable
  const minKpi = Math.min(loKpi, hiKpi);
  const maxKpi = Math.max(loKpi, hiKpi);
  if (targetValue < minKpi - Math.abs(minKpi) * 0.1 || targetValue > maxKpi + Math.abs(maxKpi) * 0.1) {
    return { found: false, value: fp[paramKey] as number, resultKpi: getKpi(fp[paramKey] as number) };
  }

  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2;
    const midKpi = getKpi(mid);
    const diff = midKpi - targetValue;

    if (Math.abs(diff) < tolerance) {
      return { found: true, value: mid, resultKpi: midKpi };
    }

    if ((ascending && diff < 0) || (!ascending && diff > 0)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const finalVal = (lo + hi) / 2;
  return { found: true, value: finalVal, resultKpi: getKpi(finalVal) };
}
